import { Component, signal, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { AuthService } from '../../core/services/auth.service';
import { ProjectContextService } from '../../core/services/project-context.service';
import { ProjectsStoreService } from '../../core/services/projects-store.service';
import { SystemApiService } from '../../core/services/system-api.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar implements OnInit, OnDestroy {
  isAdmin = signal(false);
  projectName: string = '';
  appVersion = environment.appVersion;
  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly auth: AuthService,
    private readonly projectsStore: ProjectsStoreService,
    private readonly projectContext: ProjectContextService,
    private readonly systemApi: SystemApiService,
  ) {}

  ngOnInit() {
    this.isAdmin.set(this.auth.isAdmin);
    this.loadSystemVersion();
    this.projectContext.projectId$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getPojectName();
    });
    this.projectsStore.projectUpdates$.pipe(takeUntil(this.destroy$)).subscribe((projectId) => {
      if (projectId && projectId === this.projectContext.projectId) {
        this.getPojectName(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getPojectName(forceRefresh = false) {
    const pId = this.projectContext.projectId;
    if (!pId) {
      this.projectName = '';
      return;
    }
    const cached = forceRefresh ? null : this.projectsStore.getCachedProject(pId);
    if (cached) {
      this.projectName = cached.name;
    }
    try {
      const project = await firstValueFrom(
        forceRefresh ? this.projectsStore.refreshProject(pId) : this.projectsStore.getProject(pId),
      );
      this.projectName = project.name;
    } catch {
      this.projectName = '';
    }
  }

  private async loadSystemVersion() {
    try {
      const { version } = await firstValueFrom(this.systemApi.getVersion());
      if (version?.trim()) {
        this.appVersion = version.trim();
      }
    } catch {
      this.appVersion = environment.appVersion;
    }
  }
  protected collapsed = this.readCollapsed();

  protected toggleCollapsed() {
    this.collapsed = !this.collapsed;
    this.writeCollapsed(this.collapsed);
  }

  private readCollapsed() {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('tb.sidebarCollapsed') === '1';
  }

  private writeCollapsed(value: boolean) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('tb.sidebarCollapsed', value ? '1' : '0');
  }

  protected get canManageUsers() {
    return this.auth.canManageUsers;
  }

  protected get isUser() {
    return this.auth.isUser;
  }
}
